import { useEffect, useMemo, useRef, useState } from 'react';
import { useModal } from '../useModal';
import { pbToast } from '../toast';
import { computeLayout } from '../layout';
import { P_DISC } from '../constants';
import { FormFields, FormFooter, initialValuesFromFields } from './FormFields';
import type { FieldSpec, FieldOption } from './FormFields';
import type { OrgChartDoc, Program } from '../types';

const SUB_STYLE = { margin: '2px 0 0', fontWeight: 400, fontSize: '0.82em', opacity: 0.68 };

function groupOptions(doc: OrgChartDoc): FieldOption[] {
  return doc.groups.map((g, i) => ({ value: String(i), label: g.short }));
}

function buildFields(doc: OrgChartDoc, gi: number, pi: number | null | undefined, layout: ReturnType<typeof computeLayout>): FieldSpec[] {
  const isNew = pi == null;
  const gr = doc.groups[gi];
  const pr = (isNew ? {} : (gr.programs[pi as number] || {})) as Partial<Program>;
  const ge = layout[gi];
  const pe = !isNew && ge ? ge.progs[pi as number] : undefined;
  const settings = doc.settings;

  return [
    { key: 'group', label: 'Inside group', type: 'select', value: String(gi), options: groupOptions(doc) },
    { key: 'id', label: 'Code', type: 'text', value: pr.id || '', placeholder: 'NC3', half: true },
    { key: 'short', label: 'Short name', type: 'text', value: pr.short || '', placeholder: 'NC3', half: true },
    {
      key: 'label', label: 'Disc label', type: 'textarea',
      value: (pr.label || '').replace(/\\n/g, '\n'),
      hint: 'Press Enter for a second line on the disc.',
    },
    {
      key: 'fte', label: 'FTE badge', type: 'text',
      value: pr.fte == null ? '' : String(pr.fte),
      placeholder: 'auto', hint: 'Blank = counted automatically.',
    },
    { key: 'color', label: 'Accent colour', type: 'color', value: pr.color || settings.slate },
    { section: 'Geometry' },
    { key: 'discR', label: 'Disc radius', type: 'number', value: pe ? Math.round(pe.discR) : P_DISC, half: true, min: 22, max: 260 },
    { key: 'ringR', label: 'Orbit radius', type: 'number', value: pe ? Math.round(pe.ring) : 115, half: true, min: 34, max: 460 },
    { key: 'pang', label: 'Angle from group °', type: 'number', value: pe ? Math.round(pe.pang) : 0, half: true, min: -360, max: 360 },
    { key: 'pR', label: 'Distance from group', type: 'number', value: pe ? Math.round(pe.pR) : 225, half: true, min: 0, max: 900 },
    {
      key: 'slotRot', label: 'People fan rotation °', type: 'number', value: pr.slotRot || 0,
      min: -360, max: 360, step: 5, hint: 'Rotates auto-placed people around this orbit.',
    },
    { section: 'Disc styling' },
    { key: 'fill', label: 'Disc background', type: 'color', value: pr.fill || settings.progFill, half: true },
    { key: 'textColor', label: 'Disc text colour', type: 'color', value: pr.textColor || settings.progText, half: true },
    {
      key: 'fontSize', label: 'Disc text size px', type: 'number', value: pr.fontSize || settings.progFont,
      half: true, min: 6, max: 40, step: 0.5,
    },
    { key: 'badgeColor', label: 'FTE badge colour', type: 'color', value: pr.badgeColor || '#5A6A7C', half: true },
    { section: 'Orbit ring' },
    { key: 'ringShow', label: 'Show this orbit ring', type: 'checkbox', value: pr.ringShow !== false },
    { key: 'ringColor', label: 'Ring colour', type: 'color', value: pr.ringColor || pr.color || settings.slate, half: true },
    {
      key: 'ringWidth', label: 'Ring thickness', type: 'number',
      value: pr.ringWidth != null ? pr.ringWidth : settings.ringWidth, half: true, min: 0.4, max: 14, step: 0.2,
    },
    {
      key: 'ringDash', label: 'Ring dash pattern', type: 'text', value: pr.ringDash || settings.ringDash,
      half: true, placeholder: '5 5', hint: 'e.g. 5 5 · 1 6 · blank-free line: 0',
    },
    {
      key: 'ringSpeed', label: 'Rotation seconds / cycle', type: 'number',
      value: pr.ringSpeed != null ? pr.ringSpeed : settings.ringSpeed, half: true, min: 1, max: 200,
    },
    {
      key: 'ringSpin', label: 'Rotation direction', type: 'select', value: pr.ringSpin || settings.ringSpin,
      options: [
        { value: 'normal', label: 'Clockwise' },
        { value: 'reverse', label: 'Counter-clockwise' },
        { value: 'off', label: 'No rotation' },
      ],
    },
    { section: 'Connector line (from the group disc)' },
    { key: 'wireShow', label: 'Show this connector', type: 'checkbox', value: pr.wireShow !== false },
    { key: 'wireColor', label: 'Line colour', type: 'color', value: pr.wireColor || settings.wireColor, half: true },
    {
      key: 'wireWidth', label: 'Line thickness', type: 'number',
      value: pr.wireWidth != null ? pr.wireWidth : settings.wireWidth, half: true, min: 0.4, max: 14, step: 0.2,
    },
    {
      key: 'wireDash', label: 'Line dash pattern', type: 'text', value: pr.wireDash || '', half: true,
      placeholder: 'solid', hint: 'e.g. 6 4',
    },
    {
      key: 'wireArrow', label: 'Arrowheads', type: 'select', value: pr.wireArrow || settings.wireArrow, half: true,
      options: [
        { value: 'none', label: 'None' },
        { value: 'end', label: 'Arrow at the program' },
        { value: 'start', label: 'Arrow at the group' },
        { value: 'both', label: 'Both ends' },
      ],
    },
  ];
}

interface ProgramFormValues {
  group: string;
  id: string;
  short: string;
  label: string;
  fte: string;
  color: string;
  discR: number | null;
  ringR: number | null;
  pang: number | null;
  pR: number | null;
  slotRot: number | null;
  fill: string;
  textColor: string;
  fontSize: number | null;
  badgeColor: string;
  ringShow: boolean;
  ringColor: string;
  ringWidth: number | null;
  ringDash: string;
  ringSpeed: number | null;
  ringSpin: string;
  wireShow: boolean;
  wireColor: string;
  wireWidth: number | null;
  wireDash: string;
  wireArrow: string;
}

interface ProgramEditorProps {
  doc: OrgChartDoc;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (action: any) => void;
  gi: number;
  pi?: number | null;
  onClose: () => void;
}

export function ProgramEditor({ doc, dispatch, gi, pi, onClose }: ProgramEditorProps) {
  const { openModal, closeModal, confirm } = useModal();
  const isNew = pi == null;

  const layout = useMemo(
    () => computeLayout(doc.groups, doc.sun.x, doc.sun.y),
    [doc.groups, doc.sun.x, doc.sun.y],
  );

  const fields = useMemo(() => buildFields(doc, gi, pi, layout), [doc, gi, pi, layout]);

  const [values, setValues] = useState(() => initialValuesFromFields(fields) as unknown as ProgramFormValues);
  const [error, setError] = useState<string | null>(null);
  const onChange = (key: string, value: boolean | number | null | string) =>
    setValues((v) => ({ ...v, [key]: value } as unknown as ProgramFormValues));

  const idRef = useRef(`program-editor-${gi}-${pi == null ? 'new' : pi}`);
  const id = idRef.current;

  const gr = doc.groups[gi];
  const pr = isNew ? null : (gr.programs[pi as number] || null);
  const sub = isNew ? 'A new sub-program orbit inside a group.' : `${gr.short} · ${pr ? pr.short : ''}`;

  function handleSave() {
    setError(null);
    if (!values.short.trim() && !values.label.trim()) {
      setError('Give the program a name.');
      return;
    }
    const tgi = parseInt(values.group, 10);

    const patch: Record<string, unknown> = {
      id: values.id.trim() || (values.short.trim().slice(0, 6).toUpperCase() || 'NEW'),
      short: values.short.trim() || values.label.trim().replace(/\n/g, ' '),
      color: values.color,
      discR: values.discR, ringR: values.ringR, pang: values.pang, pR: values.pR,
      slotRot: values.slotRot || 0,
      fill: values.fill, textColor: values.textColor, fontSize: values.fontSize,
      badgeColor: values.badgeColor,
      ringShow: values.ringShow, ringColor: values.ringColor, ringWidth: values.ringWidth,
      ringDash: values.ringDash, ringSpeed: values.ringSpeed, ringSpin: values.ringSpin,
      wireShow: values.wireShow, wireColor: values.wireColor, wireWidth: values.wireWidth,
      wireDash: values.wireDash, wireArrow: values.wireArrow,
    };
    patch.label = values.label.trim() || patch.short;
    if (values.fte.trim()) patch.fte = values.fte.trim();

    if (isNew) {
      dispatch({ type: 'ADD_PROGRAM', gi: tgi, program: { ...patch, people: [] } });
      pbToast(`Added program ${patch.short}`, 'ok');
    } else if (tgi !== gi) {
      if (gr.programs.length === 1) {
        setError('A group needs at least one program.');
        return;
      }
      dispatch({ type: 'UPDATE_PROGRAM', gi, pi, targetGi: tgi, patch });
      pbToast(`Moved ${patch.short} into ${doc.groups[tgi].short}`, 'ok');
    } else {
      dispatch({ type: 'UPDATE_PROGRAM', gi, pi, patch });
      pbToast('Program updated', 'ok');
    }
    closeModal(id);
  }

  function handleDelete() {
    if (isNew || !pr) return;
    if (gr.programs.length === 1) {
      pbToast('A group needs at least one program — delete the group instead.', 'err');
      return;
    }
    if (!confirm(`Delete the program "${pr.short}" and its ${pr.people.length} people?`)) return;
    dispatch({ type: 'DELETE_PROGRAM', gi, pi });
    pbToast(`Deleted program ${pr.short}`, 'ok');
    closeModal(id);
  }

  function handleCancel() {
    closeModal(id);
  }

  useEffect(() => {
    openModal({
      id,
      pageScope: 'page-org',
      title: (
        <>
          <div>{isNew ? 'Add program' : 'Edit program'}</div>
          <div style={SUB_STYLE}>{sub}</div>
        </>
      ),
      render: () => (
        <>
          <p className="merr">{error || ''}</p>
          <FormFields fields={fields} values={values as unknown as Record<string, boolean | number | null | string>} onChange={onChange} idPrefix={`${id}-`} />
        </>
      ),
      footer: (
        <FormFooter
          saveLabel={isNew ? 'Add program' : 'Save changes'}
          deleteLabel="Delete program"
          onCancel={handleCancel}
          onDelete={isNew ? undefined : handleDelete}
          onSave={handleSave}
        />
      ),
      onClose,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, fields, values, error, isNew]);

  useEffect(() => () => closeModal(id), [id, closeModal]);

  return null;
}

export default ProgramEditor;
